import { useMemo, useState } from "react";
import { Modal } from "antd";
import classNames from "classnames";
import { get, isEmpty, isNil } from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import CanvasIcon from "./CanvasIcon";
import {
  CHANGE_KINDS,
  CODE_TARGETS,
  changeTarget,
  codeEditsOf,
  isScreenChange,
  PROPOSAL_STATUSES,
  proposalChanges,
} from "./canvas-proposal";
import { STEP_TABS, stepKind } from "./step-tabs";
import { markChanges } from "./change-diff";
import { ChangeScreen, ChangeThumbnail } from "./ChangeFrame";
import { fillTemplate, WORKSHOP_ICONS } from "./workshop-view";

const frameName = (...parts) =>
  parts
    .filter(Boolean)
    .join("-")
    .replace(/[^A-Za-z0-9_-]/g, "");

const ChangePair = ({ change, frameKey, styleContent, where, labels }) => {
  const [comparing, setComparing] = useState(false);
  const marked = useMemo(
    () =>
      markChanges({
        beforeHtml: isEmpty(change.before) ? null : change.before,
        afterHtml: change.after,
        beforeStyle: change.beforeStyle || styleContent,
        afterStyle: change.afterStyle || styleContent,
      }),
    [change, styleContent],
  );
  const beforeStyle = change.beforeStyle || styleContent;
  const afterStyle = change.afterStyle || styleContent;
  const sides = [
    ...(isNil(marked.before)
      ? []
      : [
          {
            key: "before",
            label: labels.BEFORE,
            html: marked.before,
            style: beforeStyle,
          },
        ]),
    {
      key: "after",
      label: isNil(marked.before) ? labels.NEW : labels.AFTER,
      html: marked.after,
      style: afterStyle,
    },
  ];
  return (
    <div className="canvas-claude__pair-wrap">
      <button
        type="button"
        className={classNames("canvas-claude__pair", {
          "canvas-claude__pair--single": sides.length === 1,
        })}
        aria-label={labels.COMPARE}
        title={labels.COMPARE}
        onClick={() => setComparing(true)}
      >
        <span className="canvas-claude__pair-sides">
          {sides.map((side) => (
            <span key={side.key} className="canvas-claude__thumb">
              <span className="canvas-claude__thumb-cap">
                <span
                  className={`canvas-claude__thumb-dot canvas-claude__thumb-dot--${side.key}`}
                />
                {side.label}
              </span>
              <ChangeThumbnail
                name={frameName(frameKey, side.key, "thumb")}
                html={side.html}
                styleContent={side.style}
                title={side.label}
              />
            </span>
          ))}
        </span>
        <span className="canvas-claude__pair-zoom">
          <CanvasIcon path={WORKSHOP_ICONS.expand} size={14} strokeWidth={2} />
          {labels.COMPARE}
        </span>
      </button>
      <Modal
        open={comparing}
        width={1000}
        footer={null}
        destroyOnClose
        title={where}
        onCancel={() => setComparing(false)}
        className="canvas-claude__compare-modal"
      >
        <div className="canvas-claude__compare-grid">
          {sides.map((side) => (
            <div key={side.key} className="canvas-claude__compare-col">
              <span className="canvas-claude__thumb-cap">
                <span
                  className={`canvas-claude__thumb-dot canvas-claude__thumb-dot--${side.key}`}
                />
                {side.label}
              </span>
              <div className="canvas-claude__compare-box">
                <ChangeScreen
                  name={frameName(frameKey, side.key, "big")}
                  html={side.html}
                  styleContent={side.style}
                  title={side.label}
                />
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

ChangePair.propTypes = {
  change: PropTypes.object.isRequired,
  frameKey: PropTypes.string.isRequired,
  styleContent: PropTypes.string,
  where: PropTypes.string,
  labels: PropTypes.object.isRequired,
};

ChangePair.defaultProps = {
  styleContent: "",
  where: "",
};

const codeLines = (text, sign) =>
  String(text || "")
    .split("\n")
    .map((line) => `${sign} ${line}`);

const CodeEdits = ({ edits, labels }) => (
  <div className="canvas-claude__code">
    {edits.map((item, index) => (
      <div key={`${item.target}-${index}`} className="canvas-claude__code-edit">
        <span className="canvas-claude__code-target">
          {item.target === CODE_TARGETS.CSS ? labels.CSS : labels.HTML}
        </span>
        <pre className="canvas-claude__code-lines">
          {item.find
            ? codeLines(item.find, "-").map((line, rank) => (
                <span
                  key={`d${rank}`}
                  className="canvas-claude__code-line canvas-claude__code-line--del"
                >
                  {line}
                </span>
              ))
            : null}
          {codeLines(item.replace, "+").map((line, rank) => (
            <span
              key={`a${rank}`}
              className="canvas-claude__code-line canvas-claude__code-line--add"
            >
              {line}
            </span>
          ))}
        </pre>
      </div>
    ))}
  </div>
);

CodeEdits.propTypes = {
  edits: PropTypes.arrayOf(PropTypes.object).isRequired,
  labels: PropTypes.object.isRequired,
};

const ClaudeProposal = ({
  message,
  nodes,
  applying,
  refusing,
  onApply,
  onRefuse,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const CLAUDE = t("CANVAS_EDITOR.CLAUDE", { returnObjects: true }) || {};
  const PROPOSAL = CLAUDE.PROPOSAL || {};
  const STEP = t("CANVAS_EDITOR.STEP", { returnObjects: true }) || {};
  const NEXT = t("CANVAS_EDITOR.NEXT", { returnObjects: true }) || {};
  const UNNAMED = t("CANVAS_EDITOR.STEPS.UNNAMED");
  const [coded, setCoded] = useState(null);
  const changes = proposalChanges(message);
  const status = message.proposalStatus;
  const pending = status === PROPOSAL_STATUSES.PENDING;
  const busy = applying || refusing;

  const tabLabel = (tab, node) =>
    ({
      [STEP_TABS.MAIN]: get(STEP, [
        "TABS",
        "MAIN",
        stepKind(get(node, "data.nodeType")),
      ]),
      [STEP_TABS.FIELDS]: get(STEP, "TABS.FIELDS"),
      [STEP_TABS.NEXT]: NEXT.TAB,
      [STEP_TABS.SETTINGS]: get(STEP, "TABS.SETTINGS"),
    })[tab];

  const whereOf = (change, target) => {
    if (isNil(target))
      return change.kind === CHANGE_KINDS.ADD_STEP
        ? fillTemplate(PROPOSAL.NEW_STEP, { name: change.name || "" })
        : null;
    return [
      get(target, "node.data.name") || UNNAMED,
      get(target, "state.name") || null,
      tabLabel(target.tab, target.node),
    ]
      .filter(Boolean)
      .join(" › ");
  };

  const pill = {
    [PROPOSAL_STATUSES.APPLIED]: {
      tone: "applied",
      text: PROPOSAL.APPLIED_PILL,
    },
    [PROPOSAL_STATUSES.REFUSED]: {
      tone: "refused",
      text: PROPOSAL.REFUSED_PILL,
    },
  }[status];

  return (
    <div className="canvas-claude__proposal">
      <div className="canvas-claude__proposal-head">
        <strong>
          {t("CANVAS_EDITOR.CLAUDE.PROPOSAL.COUNT", { count: changes.length })}
        </strong>
        {pill ? (
          <span
            className={`canvas-claude__pill canvas-claude__pill--${pill.tone}`}
          >
            {pill.text}
          </span>
        ) : null}
      </div>
      {get(message, "proposal.summary") ? (
        <p className="canvas-claude__proposal-summary">
          {message.proposal.summary}
        </p>
      ) : null}
      <ol className="canvas-claude__changes">
        {changes.map((change, index) => {
          const key = change.id || `${index}`;
          const target = changeTarget(change, nodes);
          const where = whereOf(change, target);
          const edits = codeEditsOf(change);
          const codeOpen = coded === key;
          return (
            <li key={key} className="canvas-claude__change">
              <span className="canvas-claude__change-rank">{index + 1}</span>
              <div className="canvas-claude__change-text">
                {where ? (
                  <span className="canvas-claude__change-where">{where}</span>
                ) : null}
                <span>{change.summary || change.kind}</span>
                {edits.length ? (
                  <button
                    type="button"
                    className="canvas-claude__link"
                    aria-expanded={codeOpen}
                    onClick={() => setCoded(codeOpen ? null : key)}
                  >
                    <CanvasIcon
                      path={WORKSHOP_ICONS.code}
                      size={14}
                      strokeWidth={2}
                    />
                    {codeOpen ? PROPOSAL.CODE.HIDE : PROPOSAL.CODE.SHOW}
                  </button>
                ) : null}
              </div>
              {codeOpen ? (
                <CodeEdits edits={edits} labels={PROPOSAL.CODE} />
              ) : null}
              {isScreenChange(change) ? (
                <ChangePair
                  change={change}
                  frameKey={`${message.id}-${key}`}
                  styleContent={
                    get(target, "node.data.screenStyleContent") || ""
                  }
                  where={where || ""}
                  labels={PROPOSAL.PAIR}
                />
              ) : null}
            </li>
          );
        })}
      </ol>
      {pending ? (
        <div className="canvas-claude__decide">
          <button
            type="button"
            className="canvas-claude__apply"
            disabled={busy}
            onClick={() => onApply(message)}
          >
            {applying ? PROPOSAL.APPLYING : PROPOSAL.APPLY}
          </button>
          <button
            type="button"
            className="canvas-claude__refuse"
            disabled={busy}
            onClick={() => onRefuse(message)}
          >
            {PROPOSAL.REFUSE}
          </button>
          <span className="canvas-claude__decide-hint">{PROPOSAL.HINT}</span>
        </div>
      ) : null}
      {status === PROPOSAL_STATUSES.APPLIED ? (
        <div className="canvas-claude__outcome canvas-claude__outcome--applied">
          {PROPOSAL.APPLIED}
        </div>
      ) : null}
      {status === PROPOSAL_STATUSES.REFUSED ? (
        <div className="canvas-claude__outcome canvas-claude__outcome--refused">
          {PROPOSAL.REFUSED}
        </div>
      ) : null}
    </div>
  );
};

ClaudeProposal.propTypes = {
  message: PropTypes.object.isRequired,
  nodes: PropTypes.array,
  applying: PropTypes.bool,
  refusing: PropTypes.bool,
  onApply: PropTypes.func.isRequired,
  onRefuse: PropTypes.func.isRequired,
};

ClaudeProposal.defaultProps = {
  nodes: [],
  applying: false,
  refusing: false,
};

export default ClaudeProposal;
